import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useCoreValues } from "@/hooks/useSiteContent";
import ContentStatus from "@/components/feature/ContentStatus";

export default function AboutSection() {
  const { data: coreValues, loading, error, reload } = useCoreValues();
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="w-full bg-background-50 py-14 md:py-20"
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Image */}
          <div
            className={`relative transition-all duration-700 ${
              visible
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-8"
            }`}
          >
            <div className="relative rounded-lg overflow-hidden">
              <img
                src="https://storage.helloreaddy.io/project_files/d9750558-9174-4b87-9b66-b2ab1bef0e5e/1532c902-6fd9-4a0d-879c-9f4135677993_compressed_FB_IMG_1787391191368.webp"
                alt="Không gian Xanh Wellness"
                className="w-full h-72 md:h-[480px] object-cover object-center"
              />
              {/* Floating badge */}
              <div className="absolute bottom-4 left-4 bg-background-50/95 backdrop-blur-sm rounded-lg p-3 md:p-4 border border-background-200/70">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-award-line text-lg md:text-xl" />
                  </div>
                  <div>
                    <div className="font-display text-base md:text-lg font-bold text-foreground-950">
                      5 năm
                    </div>
                    <div className="text-xs text-foreground-600 font-label">
                      Kinh nghiệm chăm sóc sức khỏe
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Decorative element */}
            <div className="absolute -z-10 -bottom-4 -right-4 w-32 h-32 md:w-48 md:h-48 bg-accent-100/50 rounded-lg" />
          </div>

          {/* Content */}
          <div
            className={`transition-all duration-700 delay-200 ${
              visible
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-8"
            }`}
          >
            <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-secondary-100 text-secondary-800 text-xs md:text-sm font-label font-medium rounded-full mb-4">
              <i className="ri-heart-3-line" />
              Về Xanh Wellness
            </span>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              Chăm sóc từ tâm —{" "}
              <span className="text-primary-600">Khỏe đẹp tự nhiên</span>
            </h2>
            <p className="text-sm md:text-base text-foreground-600 mb-6 leading-relaxed font-body">
              Xanh Wellness ra đời với sứ mệnh mang đến giải pháp chăm sóc sức khỏe hiện đại, an toàn và hiệu quả cho mọi người. Với công nghệ cabin spa thông minh, chúng tôi giúp bạn giảm cân, giảm mỡ, thư giãn cơ thể chỉ trong 40 phút — không cần phẫu thuật, không cần tập luyện gắt gao.
            </p>
            <p className="text-sm md:text-base text-foreground-600 mb-8 leading-relaxed font-body">
              Chúng tôi tin rằng sức khỏe là nền tảng của hạnh phúc. Mỗi liệu trình đều được thiết kế tận tâm, chu đáo bởi đội ngũ chuyên viên giàu kinh nghiệm.
            </p>

            {/* Core Values */}
            <ContentStatus loading={loading} error={error} onRetry={reload}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-8">
              {coreValues.map((value) => (
                <div
                  key={value.id}
                  className="flex items-start gap-2.5 p-3 rounded-md bg-background-100 border border-background-200/50"
                >
                  <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 shrink-0">
                    <i className={`${value.icon} text-sm md:text-base`} />
                  </div>
                  <div>
                    <div className="font-label text-xs md:text-sm font-semibold text-foreground-900">
                      {value.title}
                    </div>
                    <div className="text-[11px] md:text-xs text-foreground-500 leading-snug mt-0.5">
                      {value.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </ContentStatus>

            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors"
            >
              Tìm hiểu thêm về chúng tôi
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}